import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL', americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA', basketball_ncaab: 'NCAAB',
  baseball_mlb: 'MLB', icehockey_nhl: 'NHL', soccer_usa_mls: 'MLS',
  mma_mixed_martial_arts: 'MMA', boxing_boxing: 'Boxing',
};

function StatCard({ label, value, sub, color }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2.5">
      <p className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] dark:text-[#8892a8] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{label}</p>
      <p className={`text-[22px] font-semibold ${color || 'text-[#111827] dark:text-[#e2e8f0]'}`} style={{ fontFamily: 'var(--font-sans)' }}>{value}</p>
      {sub && <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>{sub}</p>}
    </div>
  );
}

function BreakdownTable({ title, data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E7EB] dark:border-[#1e2536]">
        <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
        <h4 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{title}</h4>
      </div>
      <table className="w-full text-left">
        <thead>
          <tr className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] border-b border-[#E5E7EB] dark:border-[#1e2536]">
            <th className="py-1.5 px-3 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Name</th>
            <th className="py-1.5 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Bets</th>
            <th className="py-1.5 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Win%</th>
            <th className="py-1.5 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>P&L</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.label} className="border-b border-[#E5E7EB] dark:border-[#1e2536] text-[13px]">
              <td className="py-1.5 px-3 text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>{row.label}</td>
              <td className="py-1.5 px-2 text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{row.bets}</td>
              <td className="py-1.5 px-2 text-[#6B7280]" style={{ fontFamily: 'var(--font-mono)' }}>{row.win_rate}%</td>
              <td className={`py-1.5 px-2 font-semibold ${row.profit >= 0 ? 'text-[#029456]' : 'text-[#DC2626]'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                {row.profit >= 0 ? '+' : ''}{row.profit.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BetDashboard({ stats, bets }) {
  const plColor = stats.net_pl >= 0 ? 'text-[#029456]' : 'text-[#DC2626]';
  const roiColor = stats.roi >= 0 ? 'text-[#029456]' : 'text-[#DC2626]';
  const streakColor = stats.current_streak?.type === 'W' ? 'text-[#029456]' : stats.current_streak?.type === 'L' ? 'text-[#DC2626]' : 'text-[#6B7280]';

  const chartData = useMemo(() => {
    const resolved = bets
      .filter((b) => b.status !== 'pending' && b.status !== 'void')
      .reverse();
    let cumulative = 0;
    return resolved.map((b) => {
      cumulative += (b.profit || 0);
      return {
        date: new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        pl: Math.round(cumulative * 100) / 100,
      };
    });
  }, [bets]);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        <StatCard label="Total Wagered" value={`$${stats.total_wagered.toFixed(0)}`} />
        <StatCard label="Total Returned" value={`$${stats.total_returned.toFixed(0)}`} />
        <StatCard label="Net P&L" value={`${stats.net_pl >= 0 ? '+' : ''}$${stats.net_pl.toFixed(2)}`} color={plColor} />
        <StatCard label="ROI" value={`${stats.roi >= 0 ? '+' : ''}${stats.roi}%`} color={roiColor} />
        <StatCard label="Win Rate" value={`${stats.win_rate}%`} sub={`${stats.record.wins}W-${stats.record.losses}L-${stats.record.pushes}P`} />
        <StatCard label="Streak"
          value={stats.current_streak?.count > 0 ? `${stats.current_streak.count}${stats.current_streak.type}` : '--'}
          color={streakColor}
          sub={`Best ${stats.best_streak?.count || 0}W / Worst ${stats.worst_streak?.count || 0}L`}
        />
      </div>

      {chartData.length > 1 && (
        <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
            <h4 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>P&L Over Time</h4>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent" />
              <YAxis tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent" />
              <Tooltip
                contentStyle={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 4, fontSize: 11, fontFamily: 'var(--font-mono)' }}
                labelStyle={{ color: 'var(--tooltip-text)' }}
                formatter={(v) => [`$${v.toFixed(2)}`, 'P&L']}
              />
              <Line type="monotone" dataKey="pl" stroke="#005BB3" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <BreakdownTable title="By Sport" data={stats.by_sport?.map((r) => ({ ...r, label: SPORT_LABELS[r.label] || r.label }))} />
        <BreakdownTable title="By Market" data={stats.by_market} />
        <BreakdownTable title="By Bookmaker" data={stats.by_bookmaker} />
      </div>
    </div>
  );
}
